import { Button, Grid, Paper, Typography } from '@mui/material';
import { SiTrackPage } from '../../components/si-track-page';
import { SelectChainItem, SelectConfig, SelectResult } from '../../components/select/select-chain.component';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { workorderReportApiKeyStore } from '../../api/workorder-report-api-key-store';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { invoiceReportColumns } from './invoice-report-columns';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';

import { UserPermissionName } from '../../models/user-permission-name';
import { InvoiceFilters } from './invoice.filters';
import { invoiceReportApiKeyStore } from '../../api/invoice-report-api-key-store';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { InvoiceReport } from '../../models/invoice-report';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CustomGridCellProps {
  field: string;
}
interface CellProps {
  workorder: any;
}

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    unitID: params.get('unitID'),
  };
};
export const InvoiceReportPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const [hovered, setHovered] = useState(false);

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: '', customerName: '' },
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
      user: { userID: '', fullName: '' },
      service: { serviceID: '', serviceName: '' },
      dateCriteria: { dateCriteriaValue: '', dateCriteria: '' },
      workOrderStatus: { workOrderStatusID: '', workOrderStatus: '' },
      action: '',
      workorder: { workOrderID: 0, workorderNumber: '' },
      // scheduledDateFrom: '',
      // scheduledDateTo: '',
    },
  });

  const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(
    invoiceReportApiKeyStore.getInvoiceReports(selectedFilters),
    !!selectedFilters?.customerID ||
    !!selectedFilters?.officeID ||
    !!selectedFilters?.projectID ||
    !!selectedFilters?.unitID ||
    !!selectedFilters?.serviceID ||
    !!selectedFilters?.inspectorID ||
    !!selectedFilters?.wOStatusID ||
    !!selectedFilters?.dateFrom ||
    !!selectedFilters?.dateTo ||
    !!selectedFilters?.dateCriteria ||
    !!selectedFilters?.showAll ||
    !!selectedFilters?.workorder
    // || !!selectedFilters?.scheduledDateFrom ||
    // !!selectedFilters?.scheduledDateTo
  );

  const handleSubmit = (filtersData: any) => {
    const filteringData = {
      showAll: filtersData?.action ?? '',
      customerID: filtersData?.customer?.customerID ?? 0,
      officeID: filtersData?.office?.officeID ?? 0,
      projectID: filtersData?.project?.projectID ?? 0,
      unitID: filtersData?.unit?.unitID ?? 0,
      serviceID: filtersData?.service?.serviceID ?? 0,
      // inspectorID: filtersData?.user?.userID ?? 0,
      // wOStatusID: filtersData?.workOrderStatus?.workOrderStatusID ?? 0,
      dateFrom: filtersData?.fromDate ?? '',
      dateTo: filtersData?.toDate ?? '',
      // dateCriteria: filtersData?.dateCriteria?.dateCriteriaValue ?? '',
      workorder: filtersData?.workorder?.workorderNumber ?? 0,
      // scheduledDateFrom: filtersData?.scheduledDateFrom ?? '',
      // scheduledDateTo: filtersData?.scheduledDateTo ?? '',
    };
    setSelectedFilters(filteringData);
  };

  const handleShowAll = () => {
    form.handleSubmit(handleSubmit)();
  };

  const WorkOrderCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickWorkorderNumber = () => {
      navigate(`/workorders?searchType=workorders&searchText=${workorder?.workOrderNumber}`);
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickWorkorderNumber}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.workOrderNumber}
        </Typography>
      </td>
    );
  };

  const InvoiceAmountCell = ({ workorder }: CellProps) => {
    const invoiceAmount = workorder?.invoiceAmountNumeric;

    const formattedAmount =
      invoiceAmount !== undefined && invoiceAmount !== null
        ? `$${parseFloat(invoiceAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'workOrderNumber') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <WorkOrderCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'invoiceAmountNumeric') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <InvoiceAmountCell workorder={dataItem.dataItem} />;
      };
    }

    return undefined;
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('InvoiceReport', 'InvoiceReport')
  );

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredInvoiceReports, setFilteredInvoiceReports] = useState<InvoiceReport[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredInvoiceReportList = data?.data?.filter((InvoiceReport: any) =>
      Object.values(InvoiceReport).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredInvoiceReports(filteredInvoiceReportList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'InvoiceReport',
      pageName: 'InvoiceReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'InvoiceReport',
      pageName: 'InvoiceReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------



  return (
    <SiTrackPage title="Invoice Reports">
      <ReactHookForm formName="WORK_ORDER_REPORT_FORM" onSubmit={handleSubmit} formContextProps={form}>
        <Grid container spacing={1}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <InvoiceFilters onShowAllWorkOrder={handleShowAll} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Paper elevation={0} style={styling.paperStyle}>
              <SiTrackGrid
                fileTitle='invoice_report'
                columns={invoiceReportColumns}
                dataItemKey="workOrderID"
                // viewRoute={(value) => `/workorders?searchType=workorders&searchText=${value?.workOrderNumber}`}
                customCell={(field) =>
                  CustomGridCell({
                    field,
                  })
                }
                actionPermissions={{
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
                isBusy={isLoading || isFetching || isLoadingGridLayout}

                // -----------------Grid Layout-------
                dataItems={filteredInvoiceReports || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
              />
            </Paper>
          </Grid>
        </Grid>
      </ReactHookForm>
    </SiTrackPage>
  );
};
